import { Checkbox } from "components/ui";
import { formatDay } from "helpers/periods";
import { usePeriodStrings } from "translation/periodManagement";
import { matchPreset, summariseTakeover } from "./takeoverConfig";

const K = "period_management.takeover.summary";

/**
 * The last step before a period exists (LIX-1706).
 *
 * A takeover cannot be changed afterwards, so the selection is restated here
 * and confirmed. The point of the screen is the RIGHT column: the expensive
 * mistake is a field nobody noticed was unticked, and a list of what comes
 * along cannot surface that. Everything shown is derived from the same value
 * the form posts, never from a second source, so the two can never disagree.
 *
 * The grey block at the bottom names what nobody can influence. Without it the
 * absence of those things reads as a gap on a screen that cannot be corrected
 * afterwards: the global roles are not period bound, individually created
 * fields always come along and are deliberately not a tick, the values of the
 * period that ended stay there, and for DB the previous result is looked up
 * rather than copied.
 *
 * `areaDetails` restates a control that belongs to one area, the way areaExtras
 * offers it on the step before: LOB hangs the chosen Umgang mit den
 * Zielvereinbarungen under Leistungskarten, because the area description says
 * the objectives come along and only that choice says what happens to them.
 * Shown for a carried area only, a dropped one has no handling to describe.
 */
export default function PeriodTakeoverSummary({
  config,
  value,
  areaDetails = {},
  sourceName,
  newName,
  start,
  end,
  confirmed,
  onConfirmedChange,
}) {
  const { pt, ps } = usePeriodStrings();

  const { carried, dropped, employeesOn, fieldsCarried, fieldsDropped } = summariseTakeover(config, value);
  const total = config.fields.length;
  const preset = matchPreset(config, value);

  const carriedCount = config.fixedAreas.length + carried.length;
  const droppedCount = dropped.length + (fieldsDropped.length ? 1 : 0);

  /**
   * ps() and not pt(): the two field lists are joined into one sentence, and
   * pt() returns the editable-translation span rather than a string, which
   * joins to [object Object]. The same labels stay editable on the step before.
   */
  const fieldLabel = (f) => ps(`period_management.takeover.field.${f.key}`);
  const countLabel = (base, count) =>
    ps(count === 1 ? `${K}.${base}_one` : `${K}.${base}_other`, { count });

  const rowBody = (area, children, isDropped = false) => (
    <span className="peb-summary__row-body">
      <span className="peb-summary__row-title">{pt(area.labelKey)}</span>
      <span className="peb-summary__row-desc">
        {pt((isDropped && area.droppedDescKey) || area.summaryDescKey || area.descKey)}
      </span>
      {children}
    </span>
  );

  return (
    <div className="peb-summary" data-testid="takeover-summary">
      <p className="peb-summary__lede">{pt(`${K}.lede`)}</p>

      <div className="peb-summary__head">
        {sourceName && (
          <span className="peb-summary__kv">
            <span className="peb-summary__k">{pt(`${K}.head.source`)}</span>
            <span className="peb-summary__v" data-testid="summary-source">{sourceName}</span>
          </span>
        )}
        <span className="peb-summary__kv">
          <span className="peb-summary__k">{pt(`${K}.head.new`)}</span>
          <span className="peb-summary__v" data-testid="summary-name">{newName}</span>
        </span>
        <span className="peb-summary__kv">
          <span className="peb-summary__k">{pt("period_management.detail.evaluation_period")}</span>
          <span className="peb-summary__v" data-testid="summary-range">
            {formatDay(start)} - {formatDay(end)}
          </span>
        </span>
        {/* Without employees the visibility choice has nothing to act on, so
            stating one here would describe a decision that does not apply. */}
        {employeesOn && (
          <span className="peb-summary__kv">
            <span className="peb-summary__k">{pt(`${K}.head.visibility`)}</span>
            <span className="peb-summary__v" data-testid="summary-visibility">
              {pt(`period_management.takeover.visibility.${value.visible ? "visible" : "hidden"}`)}
            </span>
          </span>
        )}
        <span className="peb-summary__preset" data-testid="summary-preset">
          {preset
            ? pt(`period_management.copy_mode.${preset}`)
            : pt("period_management.takeover.custom")}
        </span>
      </div>

      <div className="peb-summary__cols">
        <section className="peb-summary__panel peb-summary__panel--yes" data-testid="summary-carried">
          <header className="peb-summary__panel-head">
            <i className="bi bi-check-lg" aria-hidden="true" />
            {pt(`${K}.carried_title`)}
            <span className="peb-summary__count" data-testid="summary-carried-count">
              {countLabel("areas", carriedCount)}
            </span>
          </header>
          <ul className="peb-summary__list">
            {config.fixedAreas.map((area) => (
              <li
                key={area.key}
                className="peb-summary__row is-fixed"
                data-testid={`summary-carried-${area.key}`}
              >
                <i className="bi bi-check-lg peb-summary__mark" aria-hidden="true" />
                <span className="peb-summary__row-body">
                  <span className="peb-summary__row-title">
                    {pt(area.labelKey)}
                    <span className="peb-summary__row-note">
                      {pt("period_management.takeover.area.always_note")}
                    </span>
                  </span>
                  <span className="peb-summary__row-desc">
                    {pt(area.summaryDescKey || area.descKey)}
                  </span>
                </span>
              </li>
            ))}

            {carried.map((area) => (
              <li
                key={area.key}
                className="peb-summary__row"
                data-testid={`summary-carried-${area.key}`}
              >
                <i className="bi bi-check-lg peb-summary__mark" aria-hidden="true" />
                {rowBody(area, (
                  <>
                    {area.key === config.fieldsArea && (
                      <>
                        <span className="peb-summary__detail" data-testid="summary-fields-carried">
                          {ps(`${K}.fields_carried`, { count: fieldsCarried.length, total })}
                        </span>
                        {fieldsCarried.length > 0 && (
                          <details className="peb-summary__more" data-testid="summary-fields-expander">
                            <summary>{pt(`${K}.fields_show`)}</summary>
                            <p>{fieldsCarried.map(fieldLabel).join(", ")}.</p>
                          </details>
                        )}
                      </>
                    )}
                    {area.implicitItems?.map((item) => (
                      <span
                        key={item.key}
                        className="peb-summary__implicit"
                        data-testid={`summary-implicit-${item.key}`}
                      >
                        <strong>{pt(item.labelKey)}</strong>
                        <span className="peb-summary__row-note">{pt(item.noteKey)}</span>
                        <span>{pt(item.descKey)}</span>
                      </span>
                    ))}
                    {areaDetails[area.key]}
                  </>
                ))}
              </li>
            ))}
          </ul>
        </section>

        <section className="peb-summary__panel peb-summary__panel--no" data-testid="summary-dropped">
          <header className="peb-summary__panel-head">
            <i className="bi bi-x-lg" aria-hidden="true" />
            {pt(`${K}.dropped_title`)}
            <span className="peb-summary__count" data-testid="summary-dropped-count">
              {droppedCount > 0 ? countLabel("points", droppedCount) : ""}
            </span>
          </header>

          {droppedCount === 0 ? (
            <p className="peb-summary__none" data-testid="summary-dropped-none">
              {pt(`${K}.dropped_none`)}
            </p>
          ) : (
            <ul className="peb-summary__list">
              {dropped.map((area) => (
                <li
                  key={area.key}
                  className="peb-summary__row"
                  data-testid={`summary-dropped-${area.key}`}
                >
                  <i className="bi bi-x-lg peb-summary__mark" aria-hidden="true" />
                  {rowBody(area, (
                    <span className="peb-summary__detail">
                      {area.key === config.fieldsArea
                        ? ps(`${K}.dropped_employees`, { total })
                        : pt(`${K}.dropped_empty`)}
                    </span>
                  ), true)}
                </li>
              ))}

              {fieldsDropped.length > 0 && (
                <li className="peb-summary__row" data-testid="summary-fields-dropped">
                  <i className="bi bi-x-lg peb-summary__mark" aria-hidden="true" />
                  <span className="peb-summary__row-body">
                    <span className="peb-summary__row-title">
                      {ps(`${K}.fields_dropped_title`, { count: fieldsDropped.length, total })}
                    </span>
                    <span className="peb-summary__row-desc">{pt(`${K}.fields_dropped_desc`)}</span>
                    <span className="peb-summary__detail peb-summary__detail--strong">
                      {fieldsDropped.map(fieldLabel).join(", ")}
                    </span>
                  </span>
                </li>
              )}
            </ul>
          )}
        </section>
      </div>

      <section className="peb-summary__fixed" data-testid="summary-fixed">
        <header className="peb-summary__fixed-head">
          {pt(`${K}.fixed_title`)}
          <span className="peb-summary__fixed-hint">{pt(`${K}.fixed_hint`)}</span>
        </header>
        <div className="peb-summary__fixed-rows">
          <div className="peb-summary__fixed-row" data-testid="summary-fixed-always">
            <strong>{pt(`${K}.always_title`)}</strong>
            {pt(config.alwaysBodyKey)}
          </div>
          <div className="peb-summary__fixed-row" data-testid="summary-fixed-free">
            <strong>{pt(`${K}.free_title`)}</strong>
            {pt(`${K}.free_body`)}
          </div>
          <div className="peb-summary__fixed-row" data-testid="summary-fixed-never">
            <strong>{pt(`${K}.never_title`)}</strong>
            {pt(config.neverBodyKey)}
          </div>
          {config.previousResult && (
            <div className="peb-summary__fixed-row" data-testid="summary-fixed-lookup">
              <strong>{pt(`${K}.lookup_title`)}</strong>
              {pt(`${K}.lookup_body`)}
            </div>
          )}
        </div>
      </section>

      <div className="peb-summary__confirm" data-testid="summary-confirm">
        <i className="bi bi-exclamation-circle peb-summary__confirm-icon" aria-hidden="true" />
        <div className="peb-summary__confirm-body">
          <strong>{pt("period_management.takeover.irreversible")}</strong>
          <span className="peb-summary__confirm-desc">{pt(`${K}.confirm_body`)}</span>
          <Checkbox
            className="peb-summary__tick"
            data-testid="summary-confirm-tick"
            checked={confirmed}
            onChange={(e) => onConfirmedChange(e.target.checked)}
            label={pt(`${K}.confirm_tick`)}
          />
        </div>
      </div>
    </div>
  );
}
